import React, { useState } from 'react';
import { X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import styles from './AssetRationalModal.module.sass';

const AssetRationaleModal = ({ token, strategyType, onClose, priceHistory, correlationData }) => {
  // Get styled color based on strategy
  const getStrategyColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'diversify': return '#73F6FF';
      case 'juice': return '#47ff9c';
      case 'secure': return '#9c27b0';
      case 'risk': return '#ffd700';
      default: return '#73F6FF';
    }
  };
  
  const color = getStrategyColor(strategyType);
  
  // Get strategy-specific rationale text
  const getRationaleText = () => {
    const baseText = `${token.token_symbol} was selected for the ${strategyType} strategy based on `;
    
    switch (strategyType?.toLowerCase()) {
      case 'diversify':
        return `${baseText}its correlation metrics with other assets in your portfolio. This token has demonstrated movement patterns that balance your current holdings, reducing overall portfolio volatility while maintaining potential for growth.`;
      case 'juice':
        return `${baseText}its growth potential and momentum metrics. This token has shown strong market performance indicators, with positive technical signals and increasing trading volume that suggest continued upward price action.`;
      case 'secure':
        return `${baseText}its security profile and stability metrics. This token has a well-audited contract, established history, and demonstrated resilience during market fluctuations.`;
      case 'risk':
        return `${baseText}its high potential reward profile. While this asset carries higher volatility, it shows technical indicators suggesting significant upside potential that may outweigh the risk.`;
      default:
        return `${baseText}a combination of security score, market performance, and correlation with existing assets.`;
    }
  };
  
  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modalContent} style={{ maxWidth: '640px' }}>
        <div className={styles.modalHeader} style={{ 
          borderBottom: `1px solid ${color}30`,
          background: `linear-gradient(to right, ${color}15, transparent)`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className={styles.assetIconWrapper} style={{ width: '36px', height: '36px' }}>
              {token.token_logo ? (
                <img src={token.token_logo} alt={token.token_symbol} className={styles.assetIcon} />
              ) : (
                <div className={styles.assetFallback}>{token.token_symbol[0]}</div>
              )}
            </div>
            <div>
              <h3 className={styles.modalTitle}>
                {token.token_symbol} Selection Rationale
              </h3>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                {strategyType} Strategy
              </div>
            </div>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.rationaleContainer}>
            <p className={styles.rationaleText}>
              {getRationaleText()}
            </p>
          </div>
          
          <h4 style={{ 
            fontSize: '14px', 
            color: color, 
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '500'
          }}>
            Price Performance
          </h4>
          
          <div className={styles.chartContainer} style={{ height: '240px', marginBottom: '24px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={priceHistory}>
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  stroke={color} 
                  strokeWidth={2} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <h4 style={{ 
            fontSize: '14px', 
            color: color, 
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '500'
          }}>
            Correlation Matrix
          </h4>
          
          <div className={styles.correlationTable}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid rgba(115, 246, 255, 0.1)' }}>Asset</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid rgba(115, 246, 255, 0.1)' }}>Correlation</th>
                </tr>
              </thead>
              <tbody>
                {correlationData?.map((item, index) => (
                  <tr key={index}>
                    <td style={{ 
                      padding: '8px', 
                      color: 'rgba(255,255,255,0.8)',
                      borderBottom: '1px solid rgba(115, 246, 255, 0.05)'
                    }}>
                      {item.asset}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      textAlign: 'right',
                      color: item.correlation > 0 ? '#47ff9c' : '#ff4747',
                      borderBottom: '1px solid rgba(115, 246, 255, 0.05)'
                    }}>
                      {item.correlation.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className={styles.disclaimerContainer}>
            <div className={styles.disclaimerIcon}>i</div>
            <div>
              This selection is for informational purposes only and does not constitute investment advice. Always conduct your own research before making investment decisions.
            </div>
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          <button
            onClick={() => handleTokenSelect(token, "eth")}
            className={styles.viewDetailsButton}
          >
            View Full Details
          </button>
          
          <button
            onClick={onClose}
            className={styles.closeDetailsButton}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetRationaleModal;